import type {
  Business,
  BusinessEntityType,
  BusinessFramework,
  ProfitAndLossLineKey,
  ReportingFramework,
} from '../../../types/businessApi';

/**
 * The reporting framework a set of books is kept under (PRD 83 §3), on the web
 * side.
 *
 * THE RULE, which is the api's §3.3 rule restated for this repo: a component
 * NEVER tests `framework.key` to pick a word. It asks the framework object for
 * the word. The api sends the labels precisely so that the web does not grow a
 * second copy of the rules that then disagrees with the statements — a
 * `key === 'gasb'` branch in a header is exactly the bug this file exists to
 * prevent.
 *
 * What a component may legitimately use `key` for: which option is selected in
 * a picker, and telling one framework from another in a list. Never for a
 * label, a noun or a statement name.
 */

/** The commercial bottom line, as the dashboard has always written it. */
const COMMERCIAL_SURPLUS = 'Net income';

/**
 * What the api answered before PRD 83, so a client talking to an older api — or
 * one reading a payload that predates the change — renders exactly what it used
 * to rather than blank headers. These strings are the ones the services
 * hard-coded, and `commercial_gaap` on the api answers the same.
 */
export const DEFAULT_FRAMEWORK: BusinessFramework = {
  key: 'commercial_gaap',
  name: 'Commercial GAAP',
  short_name: 'GAAP',
  entity_types: ['sole_prop', 'llc', 's_corp', 'c_corp', 'partnership', 'rental_holding'],
  equity_section_label: 'Equity',
  accumulated_surplus_label: 'Retained Earnings',
  current_period_surplus_label: 'Net Income (current year)',
  expense_noun: 'expense',
  expense_noun_plural: 'expenses',
  revenue_noun: 'revenue',
  revenue_noun_plural: 'revenue',
  statement_labels: {},
  surplus_short_label: COMMERCIAL_SURPLUS,
  surplus_question: 'Am I making money?',
  profit_and_loss_line_labels: {},
};

/** Never null: a missing payload answers the default rather than blanking a header. */
export const frameworkOf = (business: Business | null | undefined): BusinessFramework =>
  business?.framework ?? DEFAULT_FRAMEWORK;

// ---- Framework words (PRD 115 W3) ---------------------------------------------
//
// Each helper below answers one word from the framework object and falls back
// to the word the page printed before PRD 115, so a web talking to an api that
// predates a field renders exactly what it used to. None of them looks at
// `key`. Where a helper compares a word with the commercial default, it is to
// keep a page's own spelling of that word (the statements write "Net Income",
// the dashboard "Net income"), not to tell frameworks apart: any framework that
// keeps the commercial word keeps the page's word with it.

/** "expense" -> "Expense". The api sends nouns in lower case. */
export const upcaseFirst = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1);

/** "Net income" -> "net income", for a word set inside a sentence. */
export const downcaseFirst = (word: string): string =>
  word.charAt(0).toLowerCase() + word.slice(1);

/**
 * The bottom line's word as the dashboard writes it: the net income tile and
 * the P&L card's last line.
 */
export const surplusShortLabel = (framework: BusinessFramework): string =>
  framework.surplus_short_label ?? COMMERCIAL_SURPLUS;

/**
 * The bottom line's word as a statement page writes it. The statements print
 * their lines in title case ("Net Income", "Operating Income") and the api's
 * word is the dashboard's, so a framework that keeps the commercial word keeps
 * the statement's spelling of it; one with its own word ("Change in net
 * assets") prints that.
 */
export const statementSurplusLabel = (framework: BusinessFramework): string => {
  const word = surplusShortLabel(framework);
  return word === COMMERCIAL_SURPLUS ? 'Net Income' : word;
};

/**
 * A report's title in this framework, by its Reports::Catalog key, falling
 * back to the title the page passes. The web mirror of the api's
 * `FrameworkProfile#statement_label(key, default)`.
 */
export const statementTitle = (
  framework: BusinessFramework,
  catalogKey: string,
  todaysTitle: string
): string => framework.statement_labels[catalogKey] ?? todaysTitle;

/** One line of the dashboard P&L, renamed where the framework renames it. */
export const profitAndLossLineLabel = (
  framework: BusinessFramework,
  key: ProfitAndLossLineKey,
  todaysWord: string
): string => framework.profit_and_loss_line_labels?.[key] ?? todaysWord;

/**
 * What the dashboard calls money in and money out, in lower case. The trend
 * plots total income (revenue plus other income), which commercial books have
 * always called "income", against "expenses", and they keep both. A framework
 * that names either side its own way reads both of its own nouns, so the pair
 * never mixes two vocabularies ("income vs expenditures").
 */
export const flowNouns = (
  framework: BusinessFramework
): { income: string; expenses: string } => {
  const ownWords =
    framework.revenue_noun !== DEFAULT_FRAMEWORK.revenue_noun
    || framework.expense_noun_plural !== DEFAULT_FRAMEWORK.expense_noun_plural;
  return ownWords
    ? { income: framework.revenue_noun, expenses: framework.expense_noun_plural }
    : { income: 'income', expenses: 'expenses' };
};

/**
 * The framework options a business may be switched to, with the one-line
 * explanation each needs to be chosen honestly. The labels here are the only
 * framework strings the web owns, and they describe the CHOICE rather than
 * restating any statement's name — those come from the api.
 *
 * `entityTypes` is the ONE thing the web mirrors from the api's profile, and it
 * is here because a picker has to know what the framework you just selected
 * allows BEFORE the save that would tell it. It is an option set, not a label —
 * the labels still come from the api — and the api validates the pair
 * regardless, so a drift here costs a 422 rather than a wrong statement.
 */
export const FRAMEWORK_OPTIONS: {
  value: ReportingFramework;
  label: string;
  description: string;
  entityTypes: BusinessEntityType[];
}[] = [
  {
    value: 'commercial_gaap',
    label: 'Commercial GAAP',
    description: 'A business that reports equity, retained earnings and net income.',
    entityTypes: ['sole_prop', 'llc', 's_corp', 'c_corp', 'partnership', 'rental_holding'],
  },
  {
    value: 'nfp_gaap',
    label: 'Nonprofit (FASB ASC 958)',
    description:
      'A nonprofit that reports net assets with and without donor restrictions, '
      + 'a Statement of Financial Position and a Statement of Activities.',
    entityTypes: ['nonprofit'],
  },
  {
    value: 'gasb',
    label: 'Government (GASB)',
    description:
      'A government that reports fund balance and expenditures, on the governmental fund '
      + 'statements.',
    entityTypes: ['government'],
  },
];

/** The entity types a framework allows, by key — for a picker mid-change. */
export const frameworkEntityTypes = (key: ReportingFramework): BusinessEntityType[] =>
  FRAMEWORK_OPTIONS.find(option => option.value === key)?.entityTypes
  ?? DEFAULT_FRAMEWORK.entity_types;
